import { Formio } from '@formio/angular';
import { Widgets } from '@formio/js';

/** The design system dialog's own overlay container, already used for dropdown panels. */
const DIALOG_OVERLAY_CONTAINER_SELECTOR = ':scope > .cdk-overlay-container';

/** The parts of a Flatpickr instance this module uses. */
type FlatpickrInstance = {
  calendarContainer: HTMLElement;
  isOpen: boolean;
  close(): void;
  _positionCalendar(): void;
};

type FlatpickrHook = (selectedDates: Date[], dateStr: string, instance: FlatpickrInstance) => void;

/**
 * Returns where a Flatpickr calendar should be appended when its input sits inside a native
 * `<dialog>`: the dialog's overlay container, or the dialog itself. Returns null outside a dialog.
 *
 * Calendars appended to `body` render behind a modal `<dialog>` (top layer), and static calendars
 * are clipped by the dialog's scroll containers, so the calendar has to live in the dialog itself.
 */
export function getDialogCalendarHost(element: Element | null | undefined): HTMLElement | null {
  const dialog = element?.closest?.('dialog');
  if (!dialog) return null;
  return dialog.querySelector<HTMLElement>(DIALOG_OVERLAY_CONTAINER_SELECTOR) ?? dialog;
}

/**
 * Flatpickr positions appended calendars in page coordinates (viewport + scroll). Converts that
 * position to coordinates relative to the host the calendar was appended to.
 */
export function alignCalendarToHost(calendar: HTMLElement, host: HTMLElement): void {
  const hostRect = host.getBoundingClientRect();
  const top = parseFloat(calendar.style.top);
  const left = parseFloat(calendar.style.left);
  if (!Number.isNaN(top)) calendar.style.top = `${top - window.scrollY - hostRect.top}px`;
  if (!Number.isNaN(left)) calendar.style.left = `${left - window.scrollX - hostRect.left}px`;
}

/**
 * Registers a calendar widget (used by datetime fields and text fields with a calendar) that
 * appends its Flatpickr calendar to the enclosing dialog, and a Day component that uses the native
 * month select inside a dialog.
 *
 * The calendar follows its input when the dialog's content scrolls, and closes when the window
 * resizes.
 *
 * Call this once after Formio.use(premium) in the app config.
 */
export function registerFormioDateForModal(): void {
  const BaseCalendarWidget = Widgets['calendar'];
  const BaseDay = Formio.Components.components['day'];

  if (BaseCalendarWidget) {
    class CalendarInModalWidget extends BaseCalendarWidget {
      private removeDialogListeners?: () => void;

      override attach(input: HTMLInputElement) {
        const host = getDialogCalendarHost(input);
        if (!host) return super.attach(input);

        const settings = this.settings as Record<string, unknown>;
        settings['appendTo'] = host;
        settings['static'] = false;

        const attached = super.attach(input);

        // Form.io assigns onOpen during attach and creates Flatpickr later, so wrap it here.
        const formioOnOpen = settings['onOpen'] as FlatpickrHook | undefined;
        let calendar: FlatpickrInstance | undefined;
        settings['onOpen'] = ((selectedDates, dateStr, instance) => {
          calendar = instance;
          alignCalendarToHost(instance.calendarContainer, host);
          formioOnOpen?.(selectedDates, dateStr, instance);
        }) satisfies FlatpickrHook;

        const dialog = host.closest('dialog') ?? host;
        // Keep the calendar on its input while the dialog's form scrolls.
        const repositionCalendar = () => {
          if (!calendar?.isOpen) return;
          calendar._positionCalendar();
          alignCalendarToHost(calendar.calendarContainer, host);
        };
        // Flatpickr's own resize handler repositions in page coordinates, so close instead.
        const closeCalendar = () => {
          if (calendar?.isOpen) calendar.close();
        };
        this.removeDialogListeners?.();
        dialog.addEventListener('scroll', repositionCalendar, true);
        window.addEventListener('resize', closeCalendar);
        this.removeDialogListeners = () => {
          dialog.removeEventListener('scroll', repositionCalendar, true);
          window.removeEventListener('resize', closeCalendar);
        };

        return attached;
      }

      override destroy(all = false) {
        this.removeDialogListeners?.();
        this.removeDialogListeners = undefined;
        return super.destroy(all);
      }
    }
    Widgets['calendar'] = CalendarInModalWidget;
  }

  if (BaseDay) {
    class DayInModalComponent extends BaseDay {
      attach(element: HTMLElement) {
        const self = this as unknown as {
          component: { fields?: { month?: { type?: string; widget?: string } } };
          root?: { element?: HTMLElement };
        };
        const isInDialog = element?.closest?.('dialog') ?? self.root?.element?.closest?.('dialog');
        if (isInDialog && self.component?.fields?.month?.type === 'select') {
          // Force html5 for month select (same as Select fix) when in dialog
          const month = self.component.fields.month;
          if (month) month.widget = 'html5';
        }
        return super.attach(element);
      }
    }
    Formio.Components.addComponent('day', DayInModalComponent);
  }
}
